import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../App.css';

interface AddressData {
  street?: string;
  buildingNumber?: string;
  streetNumber?: string;
  city?: string;
  cityDistrict?: string;
  zipCode?: string;
}

interface AddressAutocompleteProps {
  onAddressSelect: (address: AddressData) => void;
}

interface NominatimAddress {
  house_number?: string;
  road?: string;
  neighbourhood?: string;
  suburb?: string;
  city_district?: string;
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  postcode?: string;
  country?: string;
}

interface NominatimResponse {
  place_id: number;
  display_name: string;
  name?: string;
  lat: string;
  lon: string;
  type: string;
  address: NominatimAddress;
}

interface MapySuggestItem {
  name: string;
  label: string;
  position: { lon: number; lat: number };
  type: string;
  location: string;
  address?: NominatimAddress;
}

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({ onAddressSelect }) => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<MapySuggestItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 3) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const lang = i18n.language === 'cs' ? 'cs' : 'en';
      const params = new URLSearchParams({
        q: searchQuery,
        format: 'json',
        addressdetails: '1',
        limit: '8',
        countrycodes: 'cz', // Focus on Czech Republic
        'accept-language': lang,
      });

      const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
        headers: {
          'User-Agent': 'AttendanceApp/1.0', // Required by Nominatim usage policy
        },
      });

      if (response.ok) {
        const data: NominatimResponse[] = await response.json();
        // Convert Nominatim response to our format
        const items: MapySuggestItem[] = data.map((item) => ({
          name: item.name || item.display_name.split(',')[0] || '',
          label: item.display_name,
          position: { lon: parseFloat(item.lon), lat: parseFloat(item.lat) },
          type: item.type,
          location: item.display_name,
          address: item.address,
        }));
        setSuggestions(items);
        setShowDropdown(items.length > 0);
        setSelectedIndex(-1);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [i18n.language]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const parseAddressFromItem = (item: MapySuggestItem): AddressData => {
    const address: AddressData = {};
    const addr = item.address;

    if (!addr) return address;

    // Extract zip code
    if (addr.postcode) {
      address.zipCode = addr.postcode;
    }

    // Extract city (Nominatim uses different fields)
    address.city = addr.city || addr.town || addr.village || addr.municipality || '';

    // Extract city district
    if (addr.city_district || addr.suburb) {
      address.cityDistrict = addr.city_district || addr.suburb || '';
    }

    // Extract street
    if (addr.road) {
      address.street = addr.road;
    }

    // Extract house number - Nominatim combines them, try to split
    if (addr.house_number) {
      const houseNum = addr.house_number;
      // Check for Czech format like "123/45" (building number / street number)
      const match = houseNum.match(/^(\d+[a-zA-Z]?)(?:\/(\d+[a-zA-Z]?))?$/);
      if (match) {
        address.buildingNumber = match[1];
        if (match[2]) {
          address.streetNumber = match[2];
        }
      } else {
        address.buildingNumber = houseNum;
      }
    }

    return address;
  };

  const handleSelectSuggestion = (item: MapySuggestItem) => {
    const addressData = parseAddressFromItem(item);
    onAddressSelect(addressData);
    setQuery(item.label);
    setShowDropdown(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  const handleFillClick = () => {
    if (suggestions.length > 0 && selectedIndex >= 0) {
      handleSelectSuggestion(suggestions[selectedIndex]);
    } else if (suggestions.length > 0) {
      handleSelectSuggestion(suggestions[0]);
    }
  };

  return (
    <div className="address-autocomplete-wrapper" ref={wrapperRef}>
      <div className="form-group">
        <label htmlFor="addressSearch">{t('members.addressSearch')}</label>
        <div className="address-input-row">
          <input
            type="text"
            id="addressSearch"
            placeholder={t('members.addressSearchPlaceholder')}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            autoComplete="off"
          />
          <button
            type="button"
            className="address-fill-button"
            onClick={handleFillClick}
            disabled={suggestions.length === 0}
            title={t('members.fillAddress')}
          >
            {t('members.fill')}
          </button>
        </div>
        {isLoading && <small className="form-help-text">{t('common.loading')}</small>}
      </div>

      {showDropdown && suggestions.length > 0 && (
        <ul className="address-suggestions-dropdown">
          {suggestions.map((item, index) => (
            <li
              key={`${item.label}-${index}`}
              className={`address-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSelectSuggestion(item)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="suggestion-name">{item.name}</span>
              <span className="suggestion-location">{item.location}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
